import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Loader2, ShieldCheck, AlertTriangle } from "lucide-react";
import {
  getOptimalBlueStatus,
  runOptimalBlueDscrProbe,
  runOptimalBluePricingRun,
} from "@/lib/optimal-blue.functions";

export const Route = createFileRoute("/admin/optimal-blue")({
  head: () => ({
    meta: [
      { title: "Optimal Blue diagnostics · Admin" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: ObDiagnostics,
});

function ObDiagnostics() {
  const status = useServerFn(getOptimalBlueStatus);
  const probe = useServerFn(runOptimalBlueDscrProbe);
  const pricingRun = useServerFn(runOptimalBluePricingRun);
  const [busy, setBusy] = useState<null | "status" | "probe" | "pricing">(null);
  const [output, setOutput] = useState<unknown>(null);
  const [error, setError] = useState<string | null>(null);

  const SCENARIO = {
    loanPurpose: "Purchase",
    state: "FL",
    city: "Fort Lauderdale",
    county: "Broward",
    zipCode: "33301",
    propertyType: "SingleFamily",
    numberOfUnits: "OneUnit",
    appraisedValue: 500000,
    salesPrice: 500000,
    baseLoanAmount: 375000,
    fico: 740,
    dscr: 1.2,
    loanTerm: "ThirtyYear",
    amortizationType: "Fixed",
    interestOnly: false,
    lockPeriodDays: 30,
    prepaymentPenalty: "None",
    shortTermRental: false,
    entityVesting: true,
  } as const;

  async function run(kind: "status" | "probe" | "pricing") {
    setBusy(kind);
    setError(null);
    setOutput(null);
    try {
      const res =
        kind === "status"
          ? await status({})
          : kind === "pricing"
          ? await pricingRun({ data: { ...SCENARIO } })
          : await probe({
              data: {
                loanPurpose: "Purchase",
                state: "FL",
                city: "Fort Lauderdale",
                county: "Broward",
                zipCode: "33301",
                propertyType: "SingleFamily",
                numberOfUnits: "OneUnit",
                appraisedValue: 500000,
                salesPrice: 500000,
                baseLoanAmount: 375000,
                fico: 740,
                dscr: 1.2,
                loanTerm: "ThirtyYear",
                amortizationType: "Fixed",
                interestOnly: false,
                lockPeriodDays: 30,
                prepaymentPenalty: "None",
                shortTermRental: false,
                entityVesting: true,
              },
            });
      setOutput(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Request failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Optimal Blue diagnostics</h1>
        <p className="text-sm text-muted-foreground">
          Staff-only. Runs one controlled DSCR Product Group Search against the live account so we
          can confirm exactly what is returned. Nothing here is exposed publicly.
        </p>
      </div>

      <Alert>
        <ShieldCheck className="h-4 w-4" />
        <AlertTitle>Credential handling</AlertTitle>
        <AlertDescription>
          Client ID and secret are read only from backend secrets
          (<code>OPTIMAL_BLUE_CLIENT_ID</code>, <code>OPTIMAL_BLUE_CLIENT_SECRET</code>). Tokens are
          cached server-side and never sent to the browser.
        </AlertDescription>
      </Alert>

      <Card>
        <CardHeader>
          <CardTitle>Controlled test scenario</CardTitle>
          <CardDescription>
            Purchase · Florida · Investment property · Single family · $500,000 value ·
            $375,000 loan (75% LTV) · 740 FICO · 1.20 DSCR · 30-year fixed · 30-day lock
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          <Button variant="outline" onClick={() => run("status")} disabled={busy !== null}>
            {busy === "status" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Check configuration
          </Button>
          <Button onClick={() => run("probe")} disabled={busy !== null}>
            {busy === "probe" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run live DSCR search
          </Button>
          <Button onClick={() => run("pricing")} disabled={busy !== null}>
            {busy === "pricing" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Run full pricing run (normalize + store)
          </Button>
        </CardContent>
      </Card>

      {error ? (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Request failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}

      {output ? (
        <Card>
          <CardHeader>
            <CardTitle>Sanitized response</CardTitle>
          </CardHeader>
          <CardContent>
            <pre className="max-h-[600px] overflow-auto rounded-lg bg-muted p-4 text-xs">
              {JSON.stringify(output, null, 2)}
            </pre>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
